/**
 * The DNS instructions we hand a client, in one place.
 *
 * Both the dashboard's Custom Domain page and the setup flow's domain step show
 * these records; they must never drift apart, and when the host changes there
 * has to be exactly one line to edit.
 *
 * Everything points at a single CNAME target — Cloudflare for SaaS calls it the
 * "fallback origin" — so the underlying host can move without reissuing
 * instructions to every client. The backend is authoritative about what that
 * host actually is and returns it on every custom-domain response; the constant
 * below is only the value to render before the first API answer arrives.
 *
 * There is deliberately no TXT verification record: Cloudflare for SaaS issues
 * the certificate over HTTP DV, so a working CNAME is the whole proof. (An
 * earlier version of this file showed a made-up `_foundlio` TXT value that no
 * backend ever checked.)
 */

export const DEFAULT_FALLBACK_ORIGIN = 'sites.foundlio.com';

export interface DnsRecord {
  type: 'CNAME' | 'TXT' | 'A';
  host: string;
  value: string;
  /** Registrar-specific caveat, e.g. apex CNAME → use ALIAS/ANAME instead. */
  note?: string;
}

/** Normalise whatever the client typed into a bare apex hostname. */
export function normalizeDomainInput(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .replace(/:\d+$/, '')
    .replace(/\.$/, '');
}

export function buildDnsRecords(
  domain: string,
  fallbackOrigin: string = DEFAULT_FALLBACK_ORIGIN
): DnsRecord[] {
  const apex = normalizeDomainInput(domain) || 'yourdomain.com';
  return [
    {
      type: 'CNAME',
      host: '@',
      value: fallbackOrigin,
      note: `If your registrar will not accept a CNAME on the root of ${apex}, use its ALIAS / ANAME / "CNAME flattening" option with the same value.`,
    },
    { type: 'CNAME', host: 'www', value: fallbackOrigin },
  ];
}
